import { ethers } from "ethers";
import { useMemo } from "react";
import useInfiniteSwr, { type SWRInfiniteResponse } from "swr/infinite";

import type { MarketFilterLongShortItemData } from "components/Synthetics/TableMarketFilter/MarketFilterLongShort";
import { getWrappedToken } from "config/tokens";
import { useMarketsInfoData, useTokensData } from "context/SyntheticsStateContext/hooks/globalsHooks";
import type { MarketsInfoData } from "domain/synthetics/markets/types";
import { OrderType } from "domain/synthetics/orders";
import { parseContractPrice, type TokensData } from "domain/synthetics/tokens";
import { getByKey } from "lib/objects";
import { fetchTrades, type TradeRow } from "lib/indexer/client";
import { bigNumberify } from "lib/numbers";
import { EMPTY_ARRAY } from "lib/objects";

import type { PositionTradeAction, TradeAction, TradeActionType } from "./types";
import { TradeActionType as TAT } from "./types";

export type TradeHistoryResult = {
  tradeActions?: TradeAction[];
  isLoading: boolean;
  pageIndex: number;
  setPageIndex: (...args: Parameters<SWRInfiniteResponse["setSize"]>) => void;
};

function mapV1TradeToAction(
  row: TradeRow,
  chainId: number,
  marketsInfoData: MarketsInfoData,
  tokensData: TokensData
): PositionTradeAction | undefined {
  const indexTokenAddress = ethers.getAddress(row.index_token);
  const collateralTokenAddress = ethers.getAddress(row.collateral_token);

  const indexToken = getByKey(tokensData, indexTokenAddress);
  const collateralToken = getByKey(tokensData, collateralTokenAddress);

  if (!indexToken || !collateralToken) return undefined;

  const wrappedToken = getWrappedToken(chainId);

  // Find a market whose indexToken matches — falls through when no V2 markets deployed
  const marketInfo = Object.values(marketsInfoData).find(
    (m) => m.indexToken.address.toLowerCase() === indexTokenAddress.toLowerCase()
  );

  if (!marketInfo) return undefined;

  const orderType =
    row.action === "increase"
      ? OrderType.MarketIncrease
      : row.action === "decrease"
      ? OrderType.MarketDecrease
      : OrderType.Liquidation;

  const priceBigInt = bigNumberify(row.price) ?? 0n;
  const executionPrice = parseContractPrice(priceBigInt, indexToken.decimals);

  return {
    id: String(row.id),
    eventName: TAT.OrderExecuted,
    account: row.account,
    marketAddress: marketInfo.marketTokenAddress,
    marketInfo,
    indexToken,
    swapPath: [],
    initialCollateralTokenAddress: collateralTokenAddress,
    initialCollateralToken: collateralToken,
    targetCollateralToken: collateralToken,
    initialCollateralDeltaAmount: bigNumberify(row.collateral_delta) ?? 0n,
    sizeDeltaUsd: bigNumberify(row.size_delta) ?? 0n,
    acceptablePrice: executionPrice ?? 0n,
    executionPrice: executionPrice ?? undefined,
    minOutputAmount: 0n,
    orderType,
    orderKey: row.tx_hash,
    isLong: !!row.is_long,
    pnlUsd: row.realised_pnl ? bigNumberify(row.realised_pnl) ?? undefined : undefined,
    positionFeeAmount: bigNumberify(row.fee) ?? undefined,
    shouldUnwrapNativeToken: false,
    transaction: {
      timestamp: row.block_ts,
      hash: row.tx_hash,
    },
  };
}

export function useTradeHistory(
  chainId: number,
  p: {
    account: string | null | undefined;
    forAllAccounts?: boolean;
    pageSize: number;
    fromTxTimestamp?: number;
    toTxTimestamp?: number;
    marketsDirectionsFilter?: MarketFilterLongShortItemData[];
    orderEventCombinations?: {
      eventName?: TradeActionType;
      orderType?: OrderType;
      isDepositOrWithdraw?: boolean;
    }[];
  }
): TradeHistoryResult {
  const { account, forAllAccounts, pageSize, fromTxTimestamp, toTxTimestamp } = p;
  const marketsInfoData = useMarketsInfoData();
  const tokensData = useTokensData();

  const getKey = (pageIndex: number) => {
    if (chainId && (account || forAllAccounts)) {
      return [chainId, "useTradeHistory", account, forAllAccounts, fromTxTimestamp, toTxTimestamp, pageIndex, pageSize];
    }
    return null;
  };

  const {
    data,
    error,
    size: pageIndex,
    setSize: setPageIndex,
  } = useInfiniteSwr(getKey, {
    fetcher: async (key) => {
      const idx = key[6] as number;
      const res = await fetchTrades({
        account: forAllAccounts ? undefined : account?.toLowerCase(),
        from: fromTxTimestamp,
        to: toTxTimestamp,
        page: idx + 1,
        pageSize,
      });
      return res.items;
    },
  });

  const isLoading = (!error && !data) || !marketsInfoData || !tokensData;

  const tradeActions = useMemo(() => {
    if (!data || !marketsInfoData || !tokensData) return undefined;
    const rows = data.flat() as TradeRow[];
    return rows
      .map((row) => mapV1TradeToAction(row, chainId, marketsInfoData, tokensData))
      .filter(Boolean) as TradeAction[];
  }, [data, chainId, marketsInfoData, tokensData]);

  return { tradeActions, isLoading, pageIndex, setPageIndex };
}

export async function fetchTradeActions(params: {
  chainId: number;
  pageIndex: number;
  pageSize: number;
  marketsDirectionsFilter: MarketFilterLongShortItemData[] | undefined;
  forAllAccounts: boolean | undefined;
  account: string | null | undefined;
  fromTxTimestamp: number | undefined;
  toTxTimestamp: number | undefined;
  orderEventCombinations:
    | { eventName?: TradeActionType; orderType?: OrderType; isDepositOrWithdraw?: boolean }[]
    | undefined;
  marketsInfoData: MarketsInfoData | undefined;
  tokensData: TokensData | undefined;
}): Promise<TradeAction[]> {
  const { chainId, pageIndex, pageSize, account, forAllAccounts, fromTxTimestamp, toTxTimestamp, marketsInfoData, tokensData } = params;
  if (!marketsInfoData || !tokensData) return EMPTY_ARRAY as TradeAction[];

  const res = await fetchTrades({
    account: forAllAccounts ? undefined : account?.toLowerCase(),
    from: fromTxTimestamp,
    to: toTxTimestamp,
    page: pageIndex + 1,
    pageSize,
  });

  return res.items
    .map((row) => mapV1TradeToAction(row, chainId, marketsInfoData, tokensData))
    .filter(Boolean) as TradeAction[];
}
